<template>
  <div class="lighting-zone-editor">
    <div class="zone-toolbar">
      <div class="zone-toolbar-copy">
        <strong>在效果图上圈定灯具</strong>
        <small>{{ zones.length ? `已定位 ${zones.length} 处灯具，系统会自动生成柔边受光区` : '只需圈住灯芯或灯体，不用手工框出光晕' }}</small>
      </div>
      <button
        type="button"
        class="draw-toggle"
        :class="{ active: drawingEnabled }"
        :aria-pressed="String(drawingEnabled)"
        @click="$emit('update:drawing-enabled', !drawingEnabled)"
      >
        <i :class="drawingEnabled ? 'el-icon-close' : 'el-icon-crop'"></i>
        {{ drawingEnabled ? '退出圈选' : '圈定灯具' }}
      </button>
    </div>

    <div
      ref="canvas"
      class="zone-canvas"
      :class="{ drawing: drawingEnabled, 'is-drawing': draftZone }"
      @pointerdown="beginDraw"
      @pointermove="continueDraw"
      @pointerup="finishDraw"
      @pointercancel="cancelDraw"
    >
      <img
        :src="$ossImageUrl(imageUrl, 'large')"
        alt="待标记灯光区域的原始效果图"
        draggable="false"
        @load="$emit('image-load', $event)"
      />
      <button
        v-for="(zone, index) in zones"
        :key="zone.id"
        type="button"
        class="zone-box"
        :class="[zone.state, { active: zone.id === activeZoneId }]"
        :style="zoneStyle(zone)"
        :aria-label="`区域 ${index + 1}，${zone.state === 'on' ? '点亮' : '关闭'}`"
        @pointerdown.stop.prevent="selectZone(zone.id)"
      >
        <span>{{ index + 1 }}</span>
      </button>
      <div
        v-if="draftZone"
        class="zone-box draft"
        :style="zoneStyle(draftZone)"
        aria-hidden="true"
      >
        <span>+</span>
      </div>
      <div v-if="drawingEnabled && !draftZone" class="drawing-hint">
        <i class="el-icon-crop"></i>
        按住并拖拽，只圈住要控制的灯具
      </div>
    </div>

    <div class="zone-editor-footer">
      <span>
        <i class="zone-dot on"></i> 点亮
        <i class="zone-dot off"></i> 关闭
      </span>
      <div>
        <button v-if="zones.length" type="button" class="clear-button" @click="$emit('clear')">
          清空区域
        </button>
        <button type="button" class="replace-button" @click="$emit('replace-image')">
          <i class="el-icon-refresh"></i> 更换原图
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* 灯光区域使用原图百分比坐标；画布显示坐标与提交坐标的转换是维护重点。 */
export default {
  name: 'LightingZoneEditor',
  props: {
    imageUrl: {
      type: String,
      required: true
    },
    zones: {
      type: Array,
      default: () => []
    },
    activeZoneId: {
      type: String,
      default: ''
    },
    drawingEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pointerId: null,
      startPoint: null,
      draftZone: null
    }
  },
  methods: {
    zoneStyle(zone) {
      return {
        left: `${zone.x}%`,
        top: `${zone.y}%`,
        width: `${zone.width}%`,
        height: `${zone.height}%`
      }
    },
    pointFromEvent(event) {
      const bounds = this.$refs.canvas.getBoundingClientRect()
      return {
        x: Math.min(100, Math.max(0, ((event.clientX - bounds.left) / bounds.width) * 100)),
        y: Math.min(100, Math.max(0, ((event.clientY - bounds.top) / bounds.height) * 100))
      }
    },
    rectangleFromPoints(start, end) {
      return {
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y)
      }
    },
    beginDraw(event) {
      if (!this.drawingEnabled || event.button !== 0) return
      this.pointerId = event.pointerId
      this.startPoint = this.pointFromEvent(event)
      this.draftZone = { ...this.startPoint, width: 0, height: 0 }
      this.$refs.canvas.setPointerCapture(event.pointerId)
      event.preventDefault()
    },
    continueDraw(event) {
      if (event.pointerId !== this.pointerId || !this.startPoint) return
      this.draftZone = this.rectangleFromPoints(this.startPoint, this.pointFromEvent(event))
      event.preventDefault()
    },
    finishDraw(event) {
      if (event.pointerId !== this.pointerId || !this.startPoint) return
      const rectangle = this.rectangleFromPoints(this.startPoint, this.pointFromEvent(event))
      if (rectangle.width >= 1.5 && rectangle.height >= 1.5) {
        this.$emit('create-zone', rectangle)
      }
      this.releasePointer(event.pointerId)
    },
    cancelDraw(event) {
      if (event.pointerId !== this.pointerId) return
      this.releasePointer(event.pointerId)
    },
    releasePointer(pointerId) {
      if (this.$refs.canvas && this.$refs.canvas.hasPointerCapture(pointerId)) {
        this.$refs.canvas.releasePointerCapture(pointerId)
      }
      this.pointerId = null
      this.startPoint = null
      this.draftZone = null
    },
    selectZone(zoneId) {
      this.$emit('select-zone', zoneId)
    }
  }
}
</script>

<style scoped>
.lighting-zone-editor { overflow: hidden; border: 1px solid #d9e2ee; border-radius: 7px; background: #18202a; }
.zone-toolbar { min-height: 54px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 10px 9px 13px; background: #fff; }
.zone-toolbar-copy { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.zone-toolbar-copy strong { color: #334156; font-size: 10px; }
.zone-toolbar-copy small { overflow: hidden; color: #8994a4; font-size: 8px; white-space: nowrap; text-overflow: ellipsis; }
.draw-toggle { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 5px; border: 1px solid #adc3e8; border-radius: 5px; padding: 7px 10px; background: #f7faff; color: #285cb5; font-size: 9px; font-weight: 700; }
.draw-toggle.active { border-color: #2563eb; background: #2563eb; color: #fff; }
.zone-canvas { position: relative; overflow: hidden; background: #202a36; user-select: none; touch-action: none; }
.zone-canvas.drawing { cursor: crosshair; }
.zone-canvas img { width: 100%; height: auto; display: block; pointer-events: none; -webkit-user-drag: none; }
.zone-box { position: absolute; z-index: 2; overflow: visible; border: 2px dashed; border-radius: 999px; padding: 0; background: transparent; box-shadow: 0 0 0 1px rgba(255,255,255,.42), 0 4px 14px rgba(0,0,0,.18); cursor: pointer; }
.zone-box::before { content: ""; position: absolute; inset: 0; pointer-events: none; }
.zone-box::after { content: ""; position: absolute; inset: -42%; border: 1px dashed currentColor; border-radius: 999px; opacity: .42; pointer-events: none; }
.zone-box.on { border-color: #45d777; }
.zone-box.on::before { border-radius: inherit; background: radial-gradient(ellipse,rgba(255,236,164,.22),rgba(255,224,128,.08) 58%,transparent 78%); }
.zone-box.off { border-color: #ff5d64; }
.zone-box.off::before { border-radius: inherit; background: radial-gradient(ellipse,rgba(7,14,24,.42),rgba(7,14,24,.12) 58%,transparent 78%); }
.zone-box.active { z-index: 3; outline: 2px solid #fff; outline-offset: 2px; }
.zone-box > span { width: 20px; height: 20px; position: absolute; left: 5px; top: -24px; z-index: 1; display: grid; place-items: center; border-radius: 50%; background: #fff; color: #213148; font-size: 9px; font-weight: 900; box-shadow: 0 2px 8px rgba(0,0,0,.25); }
.zone-box.draft { z-index: 4; border-style: dashed; border-color: #70a5ff; background: rgba(37,99,235,.12); box-shadow: none; pointer-events: none; }
.zone-box.draft > span { background: #2563eb; color: #fff; }
.drawing-hint { position: absolute; z-index: 5; left: 50%; top: 50%; display: flex; align-items: center; gap: 7px; transform: translate(-50%,-50%); border: 1px solid rgba(255,255,255,.18); border-radius: 5px; padding: 8px 11px; background: rgba(18,28,42,.78); color: #fff; font-size: 9px; pointer-events: none; backdrop-filter: blur(5px); }
.zone-editor-footer { min-height: 45px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 7px 9px 7px 12px; background: #fff; }
.zone-editor-footer > span { display: inline-flex; align-items: center; gap: 5px; color: #7e8998; font-size: 8px; }
.zone-editor-footer > div { display: flex; align-items: center; gap: 6px; }
.zone-dot { width: 7px; height: 7px; display: inline-block; border-radius: 50%; }
.zone-dot.on { background: #32ba63; }
.zone-dot.off { margin-left: 4px; background: #ef4d55; }
.zone-editor-footer button { border-radius: 4px; padding: 6px 8px; font-size: 8px; }
.clear-button { border: 0; background: transparent; color: #9199a5; }
.clear-button:hover { color: #d13c44; }
.replace-button { border: 1px solid #d8e0ea; background: #f7f9fc; color: #556275; }
@media (max-width: 520px) {
  .zone-toolbar { align-items: flex-start; flex-direction: column; }
  .draw-toggle { align-self: stretch; justify-content: center; }
  .zone-editor-footer { align-items: flex-start; flex-direction: column; }
  .zone-editor-footer > div { width: 100%; justify-content: flex-end; }
}
</style>
